import { PropsWithChildren, useEffect } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { useAnalyticsClient } from "../../../../../../../../analytics/wac-analytics/analytics-client";
import { useConfirmationPage } from "../../hooks/use-confirmation-page";
import { useScreenManager } from "../../hooks/use-screen-manager";

export type ScreenManagerViewProps = {
    screenOverride?: JSX.Element | undefined;
    screenFallback?: JSX.Element | undefined;
};

/**
 * Component that shows activeScreen based on the screenDictionary
 * @param {ScreenManagerViewProps} - prop to override activScreen or set fallback if the activeScreen is not found
 * @returns @type {JSX.Element}
 */
export const ScreenManagerView = ({
    screenOverride,
    screenFallback
}: ScreenManagerViewProps) => {
    const confirmationPageContext = useConfirmationPage();
    const { activeScreen, screensConfig, screenDictionary, screenList } =
        useScreenManager();

    useEffect(() => {
        if (screensConfig && activeScreen) {
            screensConfig[activeScreen]?.onMount(confirmationPageContext);
        }
    }, [activeScreen]);

    if (screenOverride) {
        return screenOverride;
    }

    return activeScreen && screenDictionary ? (
        <UiViewedAnalyticsWrapper
            actionSubjectId={activeScreen}
            containerId="ScreenManagerView"
            attributes={{
                screens: screenList
            }}
        >
            {screenDictionary[activeScreen]}
        </UiViewedAnalyticsWrapper>
    ) : (
        <UiViewedAnalyticsWrapper
            actionSubjectId="screenFallback"
            containerId="ScreenManagerView"
            attributes={{
                screens: screenList
            }}
        >
            {screenFallback}
        </UiViewedAnalyticsWrapper>
    );
};

type UiViewedAnalyticsWrapperProps = {
    actionSubjectId: string;
    containerId: string;
    attributes?: object;
} & PropsWithChildren;

/**
 * Component that fires an analytics ui viewed event on actionSubject change.
 * @param props - the props
 *
 * @returns {JSX.Element}
 */
export const UiViewedAnalyticsWrapper = ({
    actionSubjectId,
    containerId,
    attributes,
    children
}: UiViewedAnalyticsWrapperProps): JSX.Element => {
    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    useEffect(() => {
        if (!analyticsClient) {
            return;
        }

        createAnalyticsEvent({
            eventType: "ui",
            action: "viewed",
            actionSubject: "ui",
            actionSubjectId,
            containerId,
            attributes
        }).fire();
    }, [analyticsClient, actionSubjectId]);

    return <>{children}</>;
};
